import type { ColumnsType } from 'antd/es/table'

import type { CatalogRowMetrics } from 'features/catalog/hooks/use-catalog-table-metrics'
import type { FocusListOption } from 'features/catalog/types'
import { METRICS, type MetricType } from 'features/charts-v2/types'
// bully ignore[no-cross-feature-private-imports: reuses the same metric-value formatter (2dp sentiment/rating, locale-formatted counts) charts-v2's own strategies use, rather than hand-rolling a second copy. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { formatMetricValue } from 'features/charts-v2/utils/metric-format-utils'

function formatMetric(value: number | null, metricType: MetricType): string {
  return value === null ? '—' : formatMetricValue(value, metricType)
}

/**
 * Sentences/Sentiment/Rating columns, built from the per-row metrics map —
 * see `use-catalog-table-metrics.ts`'s doc comment for the join. Extracted
 * from `use-catalog-table-columns.ts` (a plain, non-`use`-prefixed module,
 * so it stays out of `hook-max-lines`' scope) purely to keep that hook file
 * under the 120-line threshold.
 */
export function buildCatalogMetricColumns(
  metricsByValue: Record<string, CatalogRowMetrics>,
): ColumnsType<FocusListOption> {
  return [
    {
      title: 'Sentences',
      key: 'sentences',
      width: 110,
      align: 'right',
      sorter: (a, b) =>
        (metricsByValue[a.value]?.sentences ?? -1) -
        (metricsByValue[b.value]?.sentences ?? -1),
      render: (_, row) =>
        formatMetric(
          metricsByValue[row.value]?.sentences ?? null,
          METRICS.SENTENCE_COUNT.id,
        ),
    },
    {
      title: 'Sentiment',
      key: 'sentiment',
      width: 110,
      align: 'right',
      sorter: (a, b) =>
        (metricsByValue[a.value]?.sentiment ?? -Infinity) -
        (metricsByValue[b.value]?.sentiment ?? -Infinity),
      render: (_, row) =>
        formatMetric(
          metricsByValue[row.value]?.sentiment ?? null,
          METRICS.SENTIMENT.id,
        ),
    },
    {
      title: 'Rating',
      key: 'rating',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        (metricsByValue[a.value]?.rating ?? -Infinity) -
        (metricsByValue[b.value]?.rating ?? -Infinity),
      render: (_, row) =>
        formatMetric(
          metricsByValue[row.value]?.rating ?? null,
          METRICS.RATING.id,
        ),
    },
  ]
}
